import { App } from "@capacitor/app";
import { getOrgIdFromToken, getUserIdFromToken } from "../api/utils";
import { config } from "../config";
import { isRealNativePlatform } from "./platform";
import { CohesiveShareAuth } from "./shareAuth";

/**
 * Human-readable labels for the signed-in account, mirrored alongside the token
 * so the extension can show which org a share is about to land in. The ids are
 * carried too, purely so the labels can be checked against the token they ship
 * with — see {@link setShareAuthIdentity}.
 */
export interface ShareAuthIdentity {
  orgId: string;
  orgName: string;
  userId: string;
  userEmail: string;
}

/**
 * Mirrors the current Auth0 access token (plus the API base URL the bundle was
 * built with) into the shared Keychain for the iOS Share Extension.
 *
 * Why a custom Keychain plugin and not `@capacitor/preferences` with a `group`:
 * the Preferences plugin's `group` option is only a key *prefix* inside
 * `UserDefaults.standard` — it never touches an App Group suite, so a share
 * extension can't read it. Do not "simplify" back to Preferences.
 *
 * Only the ~24h access token is mirrored, never the refresh token; every app
 * foreground re-mirrors a fresh token, and logout clears the mirror.
 *
 * The extension derives the org and user it uploads into from that token's
 * `c_org_id`/`c_user_id` claims, so a stale mirror doesn't just mean "signed
 * out" — it means content lands in whichever account the token belongs to.
 * Anything that changes the signed-in identity therefore has to re-mirror; see
 * {@link refreshShareAuthMirror}.
 */

let appStateListenerInstalled = false;
let warnedOnce = false;
let latestGetToken: (() => Promise<string>) | null = null;
let latestIdentity: ShareAuthIdentity | null = null;

/**
 * Set by {@link clearShareAuth} so a foreground event racing the Auth0 logout
 * can't re-mirror the token the session was just torn down for. Cleared once a
 * new session is established.
 */
let suspended = false;

/**
 * Incremented per push. A push whose token arrives after a newer push started
 * is dropped rather than written: on a native org switch the foreground event
 * and the PKCE exchange race, and without this the older (previous-org) token
 * can land in the Keychain last and win.
 */
let pushSeq = 0;

/**
 * The identity labels, but only if they describe the account `accessToken` is
 * actually for.
 *
 * `/me` and the token are refreshed by different machinery, so right after an
 * org switch one of them is briefly the other's predecessor. Labelling a share
 * sheet with the wrong org is worse than labelling it with nothing — the whole
 * point of the line is that it can be trusted — so the labels only ship when
 * the ids they were fetched under match the token's own claims.
 */
function verifiedLabels(
  accessToken: string,
): { orgName: string; userEmail: string } | undefined {
  if (!latestIdentity) return undefined;
  try {
    if (
      getOrgIdFromToken(accessToken) !== latestIdentity.orgId ||
      getUserIdFromToken(accessToken) !== latestIdentity.userId
    ) {
      return undefined;
    }
  } catch {
    // Org-less token (between signup and joining an org) — nothing to label.
    return undefined;
  }
  return {
    orgName: latestIdentity.orgName,
    userEmail: latestIdentity.userEmail,
  };
}

async function push(): Promise<void> {
  const getToken = latestGetToken;
  if (!getToken || suspended) return;
  const seq = ++pushSeq;
  try {
    const accessToken = await getToken();
    if (seq !== pushSeq || suspended) return;
    await CohesiveShareAuth.setCredentials({
      accessToken,
      apiBaseUrl: config.hosts.api,
      ...verifiedLabels(accessToken),
    });
  } catch (err) {
    if (!warnedOnce) {
      warnedOnce = true;
      console.warn("shareAuthMirror: failed to mirror credentials", err);
    }
  }
}

/** Mirror now and on every app foreground. No-op off native. */
export function mirrorShareAuth(getToken: () => Promise<string>): void {
  if (!isRealNativePlatform()) return;
  latestGetToken = getToken;
  suspended = false;
  void push();
  if (!appStateListenerInstalled) {
    appStateListenerInstalled = true;
    void App.addListener("appStateChange", ({ isActive }) => {
      if (isActive) void push();
    });
  }
}

/**
 * Re-mirror immediately, because the signed-in identity just changed.
 *
 * Native org/account switches never reload the page: "Switch organization"
 * opens Auth0 in the system browser and the deep-link callback is finished
 * in-place by `handleRedirectCallback`. Nothing that mirrors on mount re-runs
 * (`getAccessTokenSilently` keeps its identity and `isLoading` never flips),
 * and the foreground event fired by the browser sheet dismissing lands *before*
 * the token cache is swapped — so it mirrors the org the user just left. This
 * is the hook that puts the new token in the Keychain, and the sequence guard
 * in {@link push} is what stops the racing foreground push from undoing it.
 *
 * No-op off native, and safe to call before {@link mirrorShareAuth} has
 * registered a token getter.
 */
export function refreshShareAuthMirror(): void {
  if (!isRealNativePlatform()) return;
  suspended = false;
  void push();
}

/**
 * Record who the app is currently signed in as, so the extension can name the
 * destination of a share. Re-mirrors when this is new information, which also
 * covers the case where `/me` resolves the new org *after* the token did.
 *
 * No-op off native.
 */
export function setShareAuthIdentity(identity: ShareAuthIdentity): void {
  if (!isRealNativePlatform()) return;
  if (
    latestIdentity?.orgId === identity.orgId &&
    latestIdentity?.orgName === identity.orgName &&
    latestIdentity?.userId === identity.userId &&
    latestIdentity?.userEmail === identity.userEmail
  ) {
    return;
  }
  latestIdentity = identity;
  void push();
}

/** Remove mirrored credentials (call on logout). No-op off native. */
export async function clearShareAuth(): Promise<void> {
  if (!isRealNativePlatform()) return;
  // Block in-flight and foreground pushes; the token getter stays registered so
  // a later login on the same app run can re-mirror without a restart.
  suspended = true;
  pushSeq++;
  latestIdentity = null;
  try {
    await CohesiveShareAuth.clearCredentials();
  } catch {
    // Missing native plugin must never break logout.
  }
}

/** Test-only: drop the module-level mirror state between cases. */
export function resetShareAuthMirrorForTest(): void {
  appStateListenerInstalled = false;
  warnedOnce = false;
  latestGetToken = null;
  latestIdentity = null;
  suspended = false;
  pushSeq = 0;
}
